import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { isFailedCalibrationRound, CALIBRATION_REQUIREMENTS_MAX_CHARS } from '../types/calibration-api';
import CalibrationJudgingSession from '../components/sourcing/CalibrationJudgingSession';
import { RoundEndReveal } from '../components/sourcing/RoundEndReveal';
import { RefinementDiffCard } from '../components/sourcing/RefinementDiffCard';
import {
  useCalibrationCandidates,
  useJudgeCalibrationCandidate,
  useCalibrationReveal,
  useRefinementProposal,
  useAcceptRefinementProposal,
  useRejectRefinementProposal,
  useRetryCalibrationRound,
  useCalibrationJobSpec,
} from '../hooks/useApi';

/**
 * Device-local ergonomics preference, not a server setting: it has no
 * cross-device meaning, and a server round-trip would cost a mutation and a
 * migration for zero product value. Wrapped in try/catch because the
 * Chrome-extension standalone context can throw on storage access, and that
 * must never break judging. Default ON — the recruiter liked the fast path.
 */
export const AUTO_ADVANCE_STORAGE_KEY = 'rb.calibration.autoAdvance';

function readAutoAdvance(): boolean {
  try {
    return localStorage.getItem(AUTO_ADVANCE_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeAutoAdvance(next: boolean): void {
  try {
    localStorage.setItem(AUTO_ADVANCE_STORAGE_KEY, next ? 'true' : 'false');
  } catch {
    /* storage unavailable — the in-memory preference still applies this session */
  }
}

/** Best-effort human text out of whatever React Query hands back as `error`. */
function errorText(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return 'unexpected error';
}

export default function SourcingAgentCalibration() {
  const { agentId, roundId } = useParams<{ agentId: string; roundId: string }>();
  const agentIdNum = Number(agentId);
  const roundIdNum = Number(roundId);
  const { data, isLoading } = useCalibrationCandidates(agentIdNum, roundIdNum);
  // Finding I5. Not destructured to `mutate` alone any more: the judge route
  // 400s on a chip/verdict polarity mismatch, and with no error surface a
  // rejected judgment vanished — the invalidation refetched the same unjudged
  // row and the card re-rendered identically, so the only signal the recruiter
  // got was "nothing happened". Same failure shape as walkthrough item 8.
  const judgeMutation = useJudgeCalibrationCandidate(agentIdNum);
  const judge = judgeMutation.mutate;
  const [autoAdvance, setAutoAdvance] = useState(readAutoAdvance);
  // US-492. The ONE piece of state undo adds. Null means forward mode — the
  // cursor below is untouched. Non-null means the card renders that specific
  // already-judged row in edit mode.
  //
  // Why this is not the counter that failed before (see the comment at the
  // cursor): that counter indexed into `unjudged`, a list which SHRINKS by one
  // on every judgment, so incrementing it skipped every other candidate. This
  // indexes into the JUDGED list, which only ever grows. Nothing about the
  // forward cursor changes.
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  /**
   * US-504c. One-way, and owned by the PAGE rather than by the per-candidate
   * session, so the uncapped job spec is fetched at most once per round no
   * matter how many candidates are researched. A recruiter who never opens the
   * dossier never requests it at all — that is the fast path staying free.
   */
  const [hasResearched, setHasResearched] = useState(false);
  const jobSpecQuery = useCalibrationJobSpec(agentIdNum, roundIdNum, hasResearched);

  // US-379/380 (Task 6). Two-step round-end sequence: first the reveal
  // (agreement rate vs. Arbie), then — once the recruiter clicks through —
  // the refinement diff-confirm screen (or a "not enough signal yet"
  // message when Task 5's GET route 404s, matching Task 4's
  // `proposalId: null` skip path for rounds that didn't produce a proposal).
  const [showDiff, setShowDiff] = useState(false);
  // No cast. `useCalibrationCandidates` is typed with the same
  // CalibrationCandidatesResponse the route annotates its reply with, so a
  // server/client contract drift is a compile error here rather than a blank
  // card in production — which is exactly how this page shipped against an API
  // that had never been written.
  const view = data;
  // Finding m6. This used to be "every LANDED row is judged", which is a
  // different claim from "the round is over". Calibration rows are inserted one
  // at a time as each AI review finishes, so with 2 of 12 landed and both
  // judged the page entered its round-end branch, called /reveal, got a correct
  // 404, and sat on "Loading round results…". It self-healed on the next poll,
  // but the copy described a state the round was not in.
  //
  // `>=` rather than `===` mirrors checkAndCompleteRound()'s own gate, so the
  // page and the server agree on when a round is full — including after the
  // worker reconciles batchSize down following a partial failure.
  // Product decision 2 (2026-08-04). A round where every candidate's AI review
  // failed is now MARKED failed rather than deleted, precisely because this
  // URL points at it. It is terminal: there is nothing to judge, nothing to
  // reveal and no proposal to fetch.
  const roundFailed = !!view && isFailedCalibrationRound(view.round.status);
  const roundFull =
    !!view && view.candidates.length > 0 && view.candidates.length >= view.round.batchSize;
  // `!roundFailed` is belt-and-braces rather than dead code: a failed round has
  // no candidate rows today, so `roundFull` is already false — but that is a
  // consequence of HOW rounds fail, not a rule, and "complete" and "failed"
  // must never both be true for the same round.
  const roundComplete =
    !isLoading && !!view && !roundFailed && roundFull && view.candidates.every((c) => !!c.recruiterVerdict);
  const { data: revealData } = useCalibrationReveal(agentIdNum, roundComplete ? roundIdNum : 0);
  // Critical C-A: ROUND-scoped, not agent-scoped. Asking the agent-wide
  // question is what returned a previous round's accepted proposal for a round
  // that produced nothing, and the card then said "Applied — here's what
  // changed" about candidates this recruiter never judged.
  const {
    data: proposalData,
    isLoading: proposalLoading,
    settledWithoutProposal,
  } = useRefinementProposal(
    roundComplete && showDiff ? agentIdNum : undefined,
    roundComplete && showDiff ? roundIdNum : undefined,
  );
  const acceptMutation = useAcceptRefinementProposal(agentIdNum);
  const rejectMutation = useRejectRefinementProposal(agentIdNum);
  const retryMutation = useRetryCalibrationRound(agentIdNum);
  const navigate = useNavigate();

  if (isLoading || !view) return <div className="p-6">Loading calibration batch…</div>;

  const { job, round } = view;

  /**
   * The honest failed state (product decision 2, 2026-08-04).
   *
   * This branch is what replaced a deleted row. Previously
   * `process-calibration-round.ts` DELETED a round in which every candidate's
   * AI review failed, so this page — which the tuning-entry redirect sends the
   * recruiter straight into — either 404'd or, worse, fell through to "You're
   * caught up — Arbie is still screening the rest of this batch" and polled
   * forever against work that had already stopped. Same failed-vs-pending
   * honesty principle this branch already applied to sourcing matches
   * (walkthrough item 8): never render an in-progress state over a finished
   * failure.
   *
   * It returns before the round-end sequence, so no /reveal or
   * /refinement-proposal request is made for a round that can have neither.
   */
  if (roundFailed) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {job.title}
          {job.location ? ` · ${job.location}` : ''}
        </h1>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Round {round.roundNumber}</p>

        <div
          role="alert"
          className="mt-4 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-4 text-sm text-amber-900 dark:text-amber-100"
        >
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle size={16} />
            This calibration round didn&rsquo;t finish
          </p>
          {/* The server's own words. The page deliberately does not invent a
              friendlier summary: the reason is the entire reason the row was
              kept instead of deleted. */}
          <p className="mt-2">{round.failureReason ?? 'Arbie could not screen any candidates for this round.'}</p>
          <p className="mt-2">
            Nothing was saved against this round, and none of your judgments were affected. Starting again runs a
            fresh batch.
          </p>
        </div>

        {retryMutation.isError && (
          <p className="mt-3 text-sm text-rose-700 dark:text-rose-300">
            Couldn&rsquo;t start a new round ({errorText(retryMutation.error)}). Nothing was queued — try again.
          </p>
        )}

        <button
          type="button"
          className="mt-4 rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-60"
          disabled={retryMutation.isPending}
          onClick={() => {
            if (retryMutation.isPending) return;
            // Navigation happens only on a CONFIRMED enqueue. Navigating
            // optimistically would send the recruiter to a page that polls for
            // a round nothing ever queued — the dead-button shape (walkthrough
            // item 7) in a new costume.
            retryMutation.mutate(undefined, {
              onSuccess: () => navigate(`/jobs/${job.id}/sourcing/tune`),
            });
          }}
        >
          {retryMutation.isPending ? 'Starting a new round…' : 'Try again with a new batch'}
        </button>
      </div>
    );
  }
  const unjudged = view.candidates.filter((c) => !c.recruiterVerdict);

  // NO separate index counter. The list itself is the cursor: a judged
  // candidate drops out of `unjudged` on the next render after the query
  // invalidates, so `unjudged[0]` is always exactly "the next one to judge".
  // A counter incremented alongside an independently shrinking list skipped
  // every other candidate and made the round impossible to complete.
  const current = unjudged[0];

  /**
   * US-492. The back stack: every judged row, most recent first.
   *
   * `judgedAt` is the server's own record and is SET ONCE, so editing a
   * judgment does not move it to the front — the stack stays in the order the
   * recruiter actually worked through it. `id` breaks ties, because two rows
   * judged in the same clock tick would otherwise order non-deterministically
   * across refetches and the back button would wobble.
   */
  //
  // REVIEW FINDING L2: BOTH columns. The forward cursor keys off
  // `recruiterVerdict` (`unjudged`, above) while this stack keyed off
  // `judgedAt` — two predicates asserting the same fact through different
  // columns. A row with `judged_at` set and `recruiter_verdict` NULL would be
  // simultaneously `current` and `judged[0]`, so "← Previous" would open edit
  // mode on the row already on screen with a null prefill. The judge route is
  // the only writer and sets both, so this is unreachable today — but agreeing
  // with the cursor costs nothing, and "unreachable" is what the six shipped
  // defects in this epic family were each assumed to be.
  const judged = view.candidates
    .filter((c): c is typeof c & { judgedAt: string } => !!c.judgedAt && !!c.recruiterVerdict)
    .sort((a, b) => b.judgedAt.localeCompare(a.judgedAt) || b.id - a.id);

  const editingIndex = editingRowId === null ? -1 : judged.findIndex((c) => c.id === editingRowId);
  const editing = editingIndex >= 0 ? judged[editingIndex] : null;
  // From forward mode, back goes to the most recent judgment; from an edit
  // view, one step further into history. `undefined` at the end of the stack
  // is what hides the button rather than looping.
  const previousRow = judged[editingIndex + 1];

  if (!current) {
    // Nothing left to judge, but the round isn't full yet — the recruiter has
    // caught up with the AI, not finished the round. Say that, rather than
    // claiming to be loading results that do not exist yet.
    if (!roundComplete) {
      return (
        <div className="flex items-center gap-2 p-6 text-gray-600 dark:text-gray-300">
          <Loader2 size={16} className="animate-spin" />
          You&rsquo;re caught up — Arbie is still screening the rest of this batch ({round.candidateCount} of{' '}
          {round.batchSize} ready). The next candidate appears here on its own.
        </div>
      );
    }

    if (!showDiff) {
      if (!revealData) return <div className="p-6">Loading round results…</div>;
      return (
        <RoundEndReveal
          candidates={revealData.candidates.map((c) => ({
            id: c.id,
            recruiterVerdict: c.recruiterVerdict ?? 'unsure',
            aiReviewScore: c.aiReviewScore ?? 0,
          }))}
          onContinue={() => setShowDiff(true)}
        />
      );
    }

    if (proposalLoading) return <div className="p-6">Checking for a refinement proposal…</div>;

    if (!proposalData?.proposal) {
      /*
        CRITICAL C-A. This is the state a recruiter lands in whenever
        `hasEnoughSignal` says no — which the tightened rule makes materially
        more likely — and it used to be occupied by a PREVIOUS round's accepted
        proposal rendered as "Applied — here's what changed". Now the query is
        round-scoped, so reaching here means one thing: THIS round produced no
        proposal.

        Two messages, not one. Generation is asynchronous, so the hook cannot
        tell "still working" from "nothing came of it" on the first null —
        `settledWithoutProposal` is true only once polling has given up. Both
        say the judgments were saved, because the recruiter's real question
        after judging 12 candidates and seeing no change is "did that count?".
      */
      return (
        <div className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="no-refinement-for-round">
          <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {job.title}
            {job.location ? ` · ${job.location}` : ''}
          </h1>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Round {round.roundNumber}</p>

          {settledWithoutProposal ? (
            <div className="mt-4 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-sm text-gray-700 dark:text-gray-300">
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                Arbie didn&rsquo;t find a clear pattern in this round
              </p>
              <p className="mt-2">
                Your judgments were saved — every one of them. Arbie just didn&rsquo;t see a strong enough signal in
                them to propose a change to the search, so nothing about it changed.
              </p>
              <p className="mt-2">
                That is a normal outcome, not a failure: a round where you and Arbie mostly agree has little to
                teach it. The judgments still count, and they carry forward into the next round.
              </p>
            </div>
          ) : (
            <p className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <Loader2 size={16} className="animate-spin" />
              Your judgments are saved. Arbie is checking them for a pattern worth changing the search over — this
              takes a few seconds, and the page updates on its own.
            </p>
          )}
        </div>
      );
    }

    // Walkthrough item 8. The GET route intentionally falls back to the most
    // recently RESOLVED proposal when nothing is pending, so what comes back
    // here after an accept is the SAME row with status='accepted'. Passing it
    // through unread is what made a successful accept (spec v2 written, agent
    // repointed, round completed) look like a dead button — and the second
    // click 404'd into a mutation with no error surface. The card branches on
    // status; both failure and in-flight states are surfaced from here.
    const submitting = acceptMutation.isPending || rejectMutation.isPending;
    const failure = acceptMutation.isError
      ? `Couldn't apply that change (${errorText(acceptMutation.error)}). Nothing was saved — try again.`
      : rejectMutation.isError
        ? `Couldn't record that rejection (${errorText(rejectMutation.error)}). Try again.`
        : null;

    return (
      <RefinementDiffCard
        // The WHOLE response, not `proposalData.proposal`. `appliedDiff` /
        // `droppedDiffEntries` / `droppedReason` are what let the card say what
        // actually landed; picking one field out of the response here is how
        // the accepted card ended up rendering a term the server had refused
        // (review finding M7).
        data={proposalData}
        suggestedHref={`/jobs/${job.id}/sourcing`}
        isSubmitting={submitting}
        errorMessage={failure}
        onAccept={(proposalId) => {
          if (submitting) return;
          acceptMutation.mutate(proposalId);
        }}
        onReject={(proposalId) => {
          if (submitting) return;
          rejectMutation.mutate(proposalId);
        }}
      />
    );
  }

  // Item 7 / "Candidate 1 of 1". The round row records how many candidates this
  // round INTENDS to contain; rows land one at a time behind a slow AI call, so
  // `candidates.length` alone understates the denominator for minutes. `max`
  // keeps it honest if a future top-up ever adds rows past the planned size.
  // `job` and `round` are non-optional in the contract, so the defensive
  // `?.`/`??` fallbacks this used to carry were dead code that quietly said
  // "the API might not send this" about fields it always sends. `max` is not
  // defensive — it keeps the denominator honest if a future top-up round adds
  // rows past the planned batch size.
  const total = Math.max(round.batchSize, view.candidates.length);
  const judgedCount = view.candidates.length - unjudged.length;
  const stillFilling = round.candidateCount < round.batchSize;

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      {/* Item 2: job context lives on the PAGE, not the card — the card stays a
          pure judging surface. The agent's search-spec terms are deliberately
          absent: showing them turns "does this person fit the job?" into "do
          you agree with the machine?", destroying the independent signal
          calibration exists to collect. They are revealed at round end. */}
      <header className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 pb-3 pt-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {job.title}
          {job.location ? ` · ${job.location}` : ''}
        </h1>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {editing
            ? `Round ${round.roundNumber} · Editing your judgment of candidate #${editing.candidateId}`
            : `Round ${round.roundNumber} · Candidate ${judgedCount + 1} of ${total}`}
        </p>
        {/* US-492. Only while there is still something to judge: once the round
            is complete the page has moved on to the reveal / refinement
            sequence, where a proposal may already have been generated from
            these judgments, and reopening judging there is a different story
            (round 2 / re-refinement) that is deliberately out of scope. That
            scope guard is what keeps the round's completion state correct by
            construction rather than by careful bookkeeping. */}
        {previousRow && (
          <button
            type="button"
            onClick={() => setEditingRowId(previousRow.id)}
            className="mt-1 rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
          >
            ← Previous candidate
          </button>
        )}
        {stillFilling && (
          <p className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Loader2 size={12} className="animate-spin" />
            Still screening the rest of this batch — {round.candidateCount} of {round.batchSize} ready
          </p>
        )}
        {(job.requirements || job.screeningCriteriaText) && (
          <details className="mt-2 text-sm">
            <summary className="cursor-pointer text-gray-700 dark:text-gray-200">
              What this job requires
            </summary>
            {job.requirements && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">
                {job.requirements}
              </p>
            )}
            {/* US-501/504c. The cap used to be SILENT: a 2,000-character string
                ending in `…` and a genuinely 2,000-character JD are identical
                on the wire, so a recruiter could read requirement 9 of 13 and
                never learn 10–13 existed. The cap is still right for a payload
                polled every ten seconds — what was wrong was not saying so.
                The dossier holds the whole text (US-502). */}
            {job.requirementsTruncated && (
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                This is only the first{' '}
                {CALIBRATION_REQUIREMENTS_MAX_CHARS.toLocaleString('en-US')} characters of the
                requirements recorded for this job. Press{' '}
                <span className="rounded border border-b-2 border-gray-300 px-1 text-[10px] font-bold dark:border-gray-600">
                  R
                </span>{' '}
                to open the dossier, which shows the full text beside the résumé.
              </p>
            )}
            {job.screeningCriteriaText && (
              <>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Screening criteria
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">
                  {job.screeningCriteriaText}
                </p>
              </>
            )}
          </details>
        )}
      </header>

      {judgeMutation.isError && (
        <div
          role="alert"
          className="mt-4 rounded border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 p-3 text-sm text-rose-800 dark:text-rose-200"
        >
          That judgment wasn’t saved ({errorText(judgeMutation.error)}). Nothing was recorded for
          this candidate — adjust it and submit again.
        </div>
      )}

      <div className="mt-4">
        {(() => {
          // The row on screen: the one being edited, or the forward cursor.
          const shown = editing ?? current;
          return (
            <CalibrationJudgingSession
              // US-504a. The key moved here from the card, unchanged, when the
              // draft did. Remounts the whole session for each candidate:
              // without it React reuses the instance and the previous
              // candidate's verdict/note silently carries over and gets
              // submitted against the next one. It also resets the identity
              // reveal, which must be per-candidate and non-persistent. In edit
              // mode the same remount is what lets the session read its prefill
              // from props exactly once.
              key={shown.id}
              agentId={agentIdNum}
              candidate={{
                id: shown.id,
                candidateId: shown.candidateId,
                redactedResumeText: shown.redactedResumeText ?? '',
              }}
              autoAdvance={autoAdvance}
              onAutoAdvanceChange={(next) => {
                setAutoAdvance(next);
                writeAutoAdvance(next);
              }}
              // REVIEW FINDING L3: false while editing. `unjudged.length`
              // describes the FORWARD queue, which has nothing to do with the
              // row on screen in edit mode. Inert today only because
              // `isEditing` wins the label ternary inside the card — a dormant
              // wrong value, not a correct one.
              isLast={!editing && unjudged.length === 1}
              isEditing={!!editing}
              initialVerdict={editing?.recruiterVerdict ?? null}
              initialChip={editing?.reasonChip ?? null}
              initialNote={editing?.recruiterNote ?? ''}
              initialResearchSeconds={shown.researchSeconds}
              onCancel={() => setEditingRowId(null)}
              contextLine={`${job.title}${job.location ? ` · ${job.location}` : ''} · Round ${round.roundNumber} · Candidate ${judgedCount + 1} of ${total}`}
              fallbackRequirements={job.requirements}
              fallbackRequirementsTruncated={job.requirementsTruncated}
              fallbackScreeningCriteria={job.screeningCriteriaText}
              jobSpec={{
                data: jobSpecQuery.data ?? null,
                isLoading: jobSpecQuery.isLoading,
                isError: jobSpecQuery.isError,
                // REVIEW FINDING H2. `staleTime: Infinity` + `refetchOnMount:
                // false` on a query the page never unmounts means a single
                // failure strands the whole round on the truncated fallback.
                // This is the only way back.
                refetch: () => void jobSpecQuery.refetch(),
              }}
              onDossierFirstOpen={() => setHasResearched(true)}
              onJudge={(result) => {
                // The SAME judge PATCH, addressed by the row on screen. No new
                // verb: the route already writes verdict/chip/note by row id,
                // and `checkAndCompleteRound` is idempotent
                // (`status NOT IN ('complete','failed')`), so re-judging a row
                // in a finished round cannot enqueue a second — paid —
                // refinement job.
                judge(
                  {
                    candidateId: shown.id,
                    verdict: result.verdict,
                    reasonChip: result.reasonChip,
                    note: result.note,
                    // Absent, not 0, when the dossier was never opened — see
                    // JudgingResult. The route treats absent as "leave the
                    // existing record alone" (GREATEST/COALESCE), which is what
                    // lets an edit through "← Previous" keep the record that
                    // research happened without re-opening the dossier.
                    researchSeconds: result.researchSeconds,
                  },
                  // Only on a CONFIRMED write. Clearing optimistically would
                  // drop the recruiter back to the forward cursor while their
                  // edit was still in flight — and if it 400'd, the error
                  // banner would be about a candidate no longer on screen.
                  { onSuccess: () => setEditingRowId(null) },
                );
              }}
            />
          );
        })()}
      </div>
    </div>
  );
}
